/**
 * Geo binning helpers (D529). H3 is NOT a dependency of this repository, so a map of points is binned on a simple lat/lng
 * grid: square-ish cells of about `cellMeters` on a side, anchored at the south-west corner of the world so a cell key is
 * stable for the same size and reference latitude. When a fact carries an H3 cell id (D531), the caller passes those cells
 * straight to the map as regions and skips this binning.
 *
 * Pure: plain `{ lat, lng }` numbers, no map library, no country.
 */
import { isFiniteNumber } from './numbers.js';
/** Metres per degree of latitude (the mean figure; the error is under 0.6 % anywhere). */
export const METERS_PER_DEGREE = 111_320;
/** Whether a coordinate is on the sphere. */
export function isGeoPoint(lat, lng) {
    return isFiniteNumber(lat) && isFiniteNumber(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}
/** The bounds of some points, or `null` when none is valid. */
export function geoBounds(points) {
    const ok = points.filter((p) => isGeoPoint(p.lat, p.lng));
    if (ok.length === 0)
        return null;
    return {
        south: Math.min(...ok.map((p) => p.lat)),
        north: Math.max(...ok.map((p) => p.lat)),
        west: Math.min(...ok.map((p) => p.lng)),
        east: Math.max(...ok.map((p) => p.lng)),
    };
}
/** Bin points into grid cells of about `cellMeters`. Invalid points are skipped; a non-positive size is repaired to 500 m. */
export function binGeoPoints(points, options) {
    const cellMeters = isFiniteNumber(options.cellMeters) && options.cellMeters > 0 ? options.cellMeters : 500;
    const valid = points.filter((p) => isGeoPoint(p.lat, p.lng));
    const bounds = geoBounds(valid);
    const referenceLat = options.referenceLat ?? (bounds === null ? 0 : (bounds.south + bounds.north) / 2);
    const latStep = cellMeters / METERS_PER_DEGREE;
    const cosLat = Math.max(0.01, Math.cos((referenceLat * Math.PI) / 180));
    const lngStep = cellMeters / (METERS_PER_DEGREE * cosLat);
    const acc = new Map();
    for (const p of valid) {
        const row = Math.floor((p.lat + 90) / latStep);
        const col = Math.floor((p.lng + 180) / lngStep);
        const key = `${row}:${col}`;
        const cur = acc.get(key) ?? { row, col, count: 0, sum: 0 };
        cur.count += 1;
        cur.sum += isFiniteNumber(p.value) ? p.value : 1;
        acc.set(key, cur);
    }
    const cells = [...acc.entries()]
        .map(([key, c]) => {
        const south = c.row * latStep - 90;
        const west = c.col * lngStep - 180;
        return { key, row: c.row, col: c.col, lat: south + latStep / 2, lng: west + lngStep / 2, bounds: { south, north: south + latStep, west, east: west + lngStep }, count: c.count, sum: c.sum };
    })
        .sort((a, b) => a.row - b.row || a.col - b.col);
    return { cells, cellMeters, referenceLat, latStep, lngStep, bounds };
}
//# sourceMappingURL=geo-bin.js.map